import type { ConnectorProductResult } from '@/modules/connector-sdk'
import type { SearchResult } from '@/modules/search-engine'

import type { PautaProduct } from './PautaTypes'
import { PAUTA_CONNECTOR_ID } from './PautaTypes'

/**
 * PautaMapper — converts Pauta products to canonical SearchResult.
 * Never returns HTML, DOM, or raw Pauta payloads.
 */
export class PautaMapper {
  toSearchResult(
    product: PautaProduct,
    options?: { connectorName?: string; updatedAt?: string },
  ): SearchResult {
    return {
      connectorId: PAUTA_CONNECTOR_ID,
      connectorName: options?.connectorName ?? 'Pauta Distribuidora',
      productCode: product.codigo,
      manufacturer: product.fabricante,
      description: product.descricao,
      price: product.preco,
      stock: product.estoque,
      delivery: product.prazo,
      url: product.url,
      updatedAt: options?.updatedAt ?? new Date().toISOString(),
    }
  }

  toSearchResults(
    products: PautaProduct[],
    options?: { connectorName?: string; updatedAt?: string },
  ): SearchResult[] {
    return products.map((p) => this.toSearchResult(p, options))
  }

  /**
   * SDK bridge used by BaseConnector.searchProducts pipeline.
   * Embeds SearchResult-compatible fields inside ConnectorProductResult.raw.
   */
  toConnectorProduct(product: PautaProduct): ConnectorProductResult {
    const search = this.toSearchResult(product)
    return {
      sku: search.productCode,
      name: search.description,
      brand: search.manufacturer || undefined,
      price: search.price,
      currency: product.moeda || 'BRL',
      stock: search.stock,
      url: search.url || undefined,
      supplier: PAUTA_CONNECTOR_ID,
      raw: {
        delivery: search.delivery,
        searchResult: search,
      },
    }
  }

  toConnectorProducts(products: PautaProduct[]): ConnectorProductResult[] {
    return products.map((p) => this.toConnectorProduct(p))
  }
}

export const pautaMapper = new PautaMapper()
